import { createSlice, PayloadAction, createAction } from "@reduxjs/toolkit";

interface ChatSession {
    id: number|string;
    header: string;
    session_id: string;
    created_at: string;
    has_patient_context?: boolean;
    context_error?: boolean;
}

interface ChatSessionState {
    loading: boolean;
    error: string | null;
    chatSession: ChatSession | null;
    // Set to true when setChatSessionId fires while a createChatSessionRequest is
    // still in flight. Prevents the eventual createChatSessionSuccess from
    // overwriting the session the user navigated to.
    navigationOccurred: boolean;
}

const initialState: ChatSessionState = {
    loading: false,
    error: null,
    chatSession: null,
    navigationOccurred: false,
};

const chatSessionSlice = createSlice({
    name: "chatSession",
    initialState,
    reducers: {
        createChatSessionRequest: (state, _action: PayloadAction<{ partner_project_id?: string; current_consultation_id?: string; latest_consultation_id?: string; total_consultation_count?: number; no_context?: boolean } | undefined>) => {
            state.chatSession = null;
            state.loading = true;
            state.error = null;
            state.navigationOccurred = false;
        },
        createChatSessionSuccess: (state, action: PayloadAction<ChatSession>) => {
            state.loading = false;
            // If the user navigated to an existing session while this request was
            // in flight, discard the newly-created session so it doesn't overwrite
            // the correct one that was set via setChatSessionId.
            if (!state.navigationOccurred) {
                state.chatSession = action.payload;
            }
        },
        createChatSessionFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(setChatSessionId, (state, action) => {
            // If a session creation request is in flight when we navigate to an
            // existing session, flag it so createChatSessionSuccess is ignored.
            if (state.loading) {
                state.navigationOccurred = true;
                state.loading = false;
            }
            state.chatSession = {
                id: action.payload,
                header: "",
                session_id: action.payload.toString(),
                created_at: new Date().toISOString(),
            };
        });
    },
});

export const { createChatSessionRequest, createChatSessionSuccess, createChatSessionFailure } =
    chatSessionSlice.actions;

export const setChatSessionId = createAction<number|string>("chatSession/setChatSessionId");
export const chatSessionReducer = chatSessionSlice.reducer;
export const chatSessionActionTypes = chatSessionSlice.actions;
